/**
 * useGamePlayers — Realtime-backed list of player IDs for a game.
 *
 * Replaces the legacy reads of `game.players` UUID[] on the `game` row
 * (audit M8 / B7). Subscribes to `postgres_changes` on the normalized
 * `game_player` table, scoped to a single `game_id`, and re-fetches on
 * every payload. The "refetch on payload" pattern is intentionally
 * cheap: it always brings the array into agreement with the DB without
 * needing to diff INSERT / UPDATE / DELETE payloads.
 *
 * Subscribed table must be added to the `supabase_realtime` publication
 * (cf. migration 043).
 */

import { useEffect, useRef, useState } from "react"
import type { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js"

import { getGamePlayers } from "../../lib/supabase/game-player"

export const useGamePlayers = (
  gameId: string | null,
  supabase: SupabaseClient | null
): string[] => {
  const [players, setPlayers] = useState<string[]>([])
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    if (!gameId || !supabase) {
      setPlayers([])
      return
    }

    let cancelled = false

    const refetch = async () => {
      const list = await getGamePlayers(gameId, supabase)
      if (!cancelled) setPlayers(list)
    }

    // Tear down any previous subscription before opening a new one.
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }

    refetch()

    const channel = supabase
      .channel(`game-player-${gameId}`)
      .on(
        "postgres_changes" as never,
        {
          event: "*",
          schema: "public",
          table: "game_player",
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          refetch()
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      cancelled = true
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [gameId, supabase])

  return players
}
